import { defineStore } from 'pinia';
import { ref, onValue, set, get, push, remove } from 'firebase/database';
import { database } from '../firebase';

export const useKnowledgeStore = defineStore('knowledge', {
  state: () => ({
    // Knowledge Base Categories
    notes: [],
    projects: [],
    contacts: [],
    ideas: [],
    
    // Dynamic Categories
    dynamicCategories: {}, // { "travel": [], "cooking": [], etc... }
    
    // Notifications for carousel feed
    notifications: [], // [{ id, type, title, message, createdAt, dismissed }]
    
    // UI State
    selectedCategory: 'all',
    searchQuery: '',
    isLoading: false,
    
    // Filters and sorting
    sortBy: 'date', // date, title, priority
    sortOrder: 'desc', // asc, desc
    
    // Firebase state
    currentUserId: null,
    isInitialized: false,
    userProfile: null
    ,
    // Tag index: { tag: { notes: [ids], projects: [ids], contacts: [ids], ideas: [ids], webroll: [ids] } }
    tagIndex: {}
  }),

  getters: {
    // Get all items across categories
    allItems() {
      const all = [
        ...(this.notes || []).map(item => ({ ...item, category: 'note' })),
        ...(this.projects || []).map(item => ({ ...item, category: 'project' })),
        ...(this.contacts || []).map(item => ({ ...item, category: 'contact' })),
        ...(this.ideas || []).map(item => ({ ...item, category: 'idea' }))
      ];
      
      // Add dynamic categories (including singular forms that may have been stored separately)
      if (this.dynamicCategories) {
        Object.entries(this.dynamicCategories).forEach(([categoryName, items]) => {
          if (Array.isArray(items)) {
            items.forEach(item => {
              // Skip if this item is already included from plural categories
              const existingItem = all.find(existing => existing.id === item.id);
              if (existingItem) {
                return; // Skip duplicate
              }
              
              // Normalize category names to standard singular form
              let normalizedCategory = categoryName;
              if (categoryName === 'notes') normalizedCategory = 'note';
              if (categoryName === 'projects') normalizedCategory = 'project'; 
              if (categoryName === 'contacts') normalizedCategory = 'contact';
              if (categoryName === 'ideas') normalizedCategory = 'idea';
              
              all.push({ ...item, category: normalizedCategory });
            });
          }
        });
      }
      
      // Add active notifications to the mix
      if (this.notifications) {
        const activeNotifications = this.notifications
          .filter(notification => !notification.dismissed)
          .map(notification => ({
            ...notification,
            category: 'notification',
            title: notification.title,
            message: notification.message,
            description: notification.message
          }));
        all.push(...activeNotifications);
      }
      
      // Sort everything by date (most recent first) - unified chronological order
      all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      return all;
    },

    // Get recent items (last 10)
    recentItems() {
      const items = this.allItems;
      return Array.isArray(items) ? items.slice(0, 10) : [];
    },

    // Get filtered items based on category and search
    filteredItems() {
      let items = this.selectedCategory === 'all' 
        ? this.allItems 
        : this.allItems.filter(item => item.category === this.selectedCategory);

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        items = items.filter(item => 
          item.title.toLowerCase().includes(query) ||
          (item.content && item.content.toLowerCase().includes(query)) ||
          (item.description && item.description.toLowerCase().includes(query))
        );
      }

      return items;
    },

    // Statistics
    stats() {
      return {
        totalItems: this.allItems.length,
        notesCount: this.notes.length,
        projectsCount: this.projects.length,
        contactsCount: this.contacts.length,
        ideasCount: this.ideas.length
      };
    }
  },

  actions: {
    // Normalize tag to canonical lowercase form
    _normalizeTag(tag) {
      return String(tag || '').trim().toLowerCase();
    },

    // Resolve plural category key for item category
    _toPluralCategory(categoryOrSingular) {
      const map = { note: 'notes', project: 'projects', contact: 'contacts', idea: 'ideas', webroll: 'webroll', urls: 'webroll', url: 'webroll' };
      const key = (categoryOrSingular || '').toString().toLowerCase();
      return map[key] || key;
    },

    // Find an item by id within a plural category (including webroll)
    _findItemById(pluralCategory, id) {
      if (pluralCategory === 'webroll') {
        const arr = this.dynamicCategories?.webroll || [];
        return arr.find(it => it.id === id) || null;
      }
      const arr = this[pluralCategory] || [];
      return arr.find(it => it.id === id) || null;
    },

    async addTagToItem(pluralCategory, itemId, tag) {
      const category = this._toPluralCategory(pluralCategory);
      const normalized = this._normalizeTag(tag);
      if (!normalized) return false;
      const existing = this._findItemById(category, itemId);
      if (!existing) return false;
      const currentTags = Array.isArray(existing.tags) ? existing.tags.map(t => this._normalizeTag(t)) : [];
      if (currentTags.includes(normalized)) return true; // no-op
      const newTags = [...currentTags, normalized];
      const updated = { ...existing, tags: newTags };
      await this.updateItem(category, itemId, updated);
      return true;
    },

    async removeTagFromItem(pluralCategory, itemId, tag) {
      const category = this._toPluralCategory(pluralCategory);
      const normalized = this._normalizeTag(tag);
      if (!normalized) return false;
      const existing = this._findItemById(category, itemId);
      if (!existing) return false;
      const currentTags = Array.isArray(existing.tags) ? existing.tags.map(t => this._normalizeTag(t)) : [];
      if (!currentTags.includes(normalized)) return true; // no-op
      const newTags = currentTags.filter(t => t !== normalized);
      const updated = { ...existing, tags: newTags };
      await this.updateItem(category, itemId, updated);
      return true;
    },

    // Update only tags of an item (safe merge)
    async updateItemTags(pluralCategory, itemId, nextTags) {
      if (!this.currentUserId || !itemId) return false;
      const category = this._toPluralCategory(pluralCategory);
      const normalized = Array.isArray(nextTags) ? nextTags.map(t => this._normalizeTag(t)) : [];
      const itemRef = ref(database, `users/${this.currentUserId}/knowledge/${category}/${itemId}`);
      // Read current, merge only tags
      const snap = await get(itemRef);
      if (!snap.exists()) return false;
      const current = snap.val();
      const updated = { ...current, tags: normalized, updatedAt: new Date().toISOString() };
      await set(itemRef, updated);
      this.rebuildTagIndex();
      return true;
    },

    // Rename a tag across items; scope: 'global' or one of plural categories; if dryRun, returns preview only
    async renameTag(oldTag, newTag, scope = 'global', { dryRun = false } = {}) {
      const from = this._normalizeTag(oldTag);
      const to = this._normalizeTag(newTag);
      if (!from || !to || from === to) return { changed: 0, counts: {}, idsByCategory: {} };
      const buckets = this.tagIndex?.[from] || { notes: [], projects: [], contacts: [], ideas: [], webroll: [] };
      const targets = {};
      const consider = (cat) => {
        if (scope === 'global' || scope === cat) targets[cat] = buckets[cat] || [];
      };
      consider('notes'); consider('projects'); consider('contacts'); consider('ideas'); consider('webroll');
      const idsByCategory = {};
      let changed = 0;
      for (const [cat, ids] of Object.entries(targets)) {
        idsByCategory[cat] = [...ids];
        if (dryRun) continue;
        for (const id of ids) {
          const item = this._findItemById(cat, id);
          if (!item) continue;
          const current = Array.isArray(item.tags) ? item.tags.map(t => this._normalizeTag(t)) : [];
          if (!current.includes(from)) continue;
          const replaced = current.map(t => (t === from ? to : t));
          const updated = { ...item, tags: Array.from(new Set(replaced)) };
          await this.updateItem(cat, id, updated);
          changed++;
        }
      }
      const counts = Object.fromEntries(Object.entries(idsByCategory).map(([k, arr]) => [k, (arr || []).length]));
      return { changed, counts, idsByCategory };
    },
    // Build/refresh in-memory tag index from current items (includes URLs under 'webroll')
    rebuildTagIndex() {
      const index = {};
      const items = this.allItems || [];
      for (const item of items) {
        const tags = Array.isArray(item.tags) ? item.tags : [];
        if (!tags.length) continue;
        // Determine canonical category bucket
        let bucket = item.category;
        if (item.type === 'url' || bucket === 'webroll') bucket = 'webroll';
        if (!['note','project','contact','idea','webroll'].includes(bucket)) continue;
        for (const raw of tags) {
          const tag = String(raw).trim().toLowerCase();
          if (!tag) continue;
          if (!index[tag]) {
            index[tag] = { notes: [], projects: [], contacts: [], ideas: [], webroll: [] };
          }
          const arr = bucket === 'note' ? index[tag].notes
            : bucket === 'project' ? index[tag].projects
            : bucket === 'contact' ? index[tag].contacts
            : bucket === 'idea' ? index[tag].ideas
            : index[tag].webroll;
          if (!arr.includes(item.id)) arr.push(item.id);
        }
      }
      this.tagIndex = index;
    },
    // Initialize for user (like podcast store)
    async initializeForUser(userId) {
      if (this.isInitialized && this.currentUserId === userId) {
        console.log('Knowledge store already initialized for user:', userId);
        return;
      }
      
      this.currentUserId = userId;
      this.isLoading = true;
      
      try {
        console.log('🔄 Initializing knowledge store for user:', userId);
        
        // Load user profile first
        await this.loadUserProfile();
        
        // Load all knowledge data
        await this.loadAllData();
        // Initial tag index build after data load
        this.rebuildTagIndex();
        
        this.isInitialized = true;
        console.log('✅ Knowledge store initialized successfully');
        const allItems = this.allItems;
        console.log('📊 Loaded items:', Array.isArray(allItems) ? allItems.length : 0);
      } catch (error) {
        console.error('❌ Error initializing KB for user:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // Load user profile (like checking for existing data)
    async loadUserProfile() {
      if (!this.currentUserId) return false;
      
      try {
        const profileRef = ref(database, `users/${this.currentUserId}/profile`);
        const snapshot = await get(profileRef);
        
        if (snapshot.exists()) {
          this.userProfile = snapshot.val();
          console.log('✅ User profile loaded:', this.userProfile);
          return true;
        } else {
          console.log('❌ No user profile found');
          return false;
        }
      } catch (error) {
        console.error('❌ Error loading user profile:', error);
        return false;
      }
    },

    // Load all data with real-time listeners (like podcast store)
    async loadAllData() {
      if (!this.currentUserId) return;
      
      try {
        // Set up real-time listeners for each category
        this.setupRealtimeListener('notes');
        this.setupRealtimeListener('projects'); 
        this.setupRealtimeListener('contacts');
        this.setupRealtimeListener('ideas');
        
        // Set up listeners for dynamic categories
        this.loadDynamicCategories();
        
        console.log('✅ Real-time listeners set up for all categories');
      } catch (error) {
        console.error('❌ Error setting up real-time listeners:', error);
      }
    },

    // Set up real-time listener for a category
    setupRealtimeListener(category) {
      const categoryRef = ref(database, `users/${this.currentUserId}/knowledge/${category}`);
      
      onValue(categoryRef, (snapshot) => {
        const data = snapshot.val() || {};
        const items = Object.entries(data).map(([id, item]) => ({
          id,
          ...item
        }));
        
        // Check if this is a standard category or dynamic category
        const standardCategories = ['notes', 'projects', 'contacts', 'ideas'];
        const singularToPlural = {
          'note': 'notes',
          'project': 'projects', 
          'contact': 'contacts',
          'idea': 'ideas'
        };
        
        if (standardCategories.includes(category)) {
          // Direct plural category mapping
          this[category] = items;
        } else if (singularToPlural[category]) {
          // Map singular to plural state property - MERGE instead of overwrite
          const pluralCategory = singularToPlural[category];
          console.log(`🔄 Mapped singular '${category}' to plural '${pluralCategory}'`);
          
          // Merge with existing plural data instead of overwriting
          const existingItems = this[pluralCategory] || [];
          const mergedItems = [...existingItems];
          
          // Add items from singular category that don't already exist
          items.forEach(item => {
            const existingItem = mergedItems.find(existing => existing.id === item.id);
            if (!existingItem) {
              mergedItems.push(item);
            }
          });
          
          this[pluralCategory] = mergedItems;
          console.log(`🔄 Merged ${items.length} items from '${category}' into '${pluralCategory}', total: ${mergedItems.length}`);
        } else {
          // For truly dynamic categories (like 'webroll')
          this.dynamicCategories[category] = items;
        }
        
        console.log(`📊 ${category} updated:`, items.length, 'items');
        // Refresh tag index after each update batch
        this.rebuildTagIndex();
      });
    },

    // Add item to Realtime Database
    async addItem(category, item) {
      if (!this.currentUserId) {
        console.error('❌ No user ID - cannot add item');
        return null;
      }

      const newItem = {
        category,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userId: this.currentUserId,
        ...item
      };

      try {
        const categoryRef = ref(database, `users/${this.currentUserId}/knowledge/${category}`);
        const newItemRef = push(categoryRef, newItem);
        
        console.log(`✅ Added ${category}:`, newItem.title || newItem.name);
        return { id: newItemRef.key, ...newItem };
      } catch (error) {
        console.error(`❌ Error adding ${category}:`, error);
        throw error;
      }
    },

    // Specific add methods
    async addNote(noteData) {
      const result = await this.addItem('notes', {
        title: noteData.title || 'Untitled Note',
        content: noteData.content || '',
        tags: noteData.tags || []
      });
      
      // Add green notification for note creation
      if (result) {
        this.addNotification(
          'note-created',
          '📝 Note Created',
          `"${result.title}" added to your notes`,
          { noteId: result.id, noteTitle: result.title }
        );
        this.rebuildTagIndex();
      }
      
      return result;
    },

    async addProject(projectData) {
      const result = await this.addItem('projects', {
        name: projectData.name || 'Untitled Project',
        description: projectData.description || '',
        status: projectData.status || 'planning',
        priority: projectData.priority || 'medium',
        tasks: projectData.tasks || [],
        tags: projectData.tags || []
      });
      this.rebuildTagIndex();
      return result;
    },

    async addContact(contactData) {
      const result = await this.addItem('contacts', {
        name: contactData.name || 'Unnamed Contact',
        email: contactData.email || '',
        phone: contactData.phone || '',
        company: contactData.company || '',
        notes: contactData.notes || '',
        tags: contactData.tags || []
      });
      this.rebuildTagIndex();
      return result;
    },

    async addIdea(ideaData) {
      const result = await this.addItem('ideas', {
        title: ideaData.title || 'Untitled Idea',
        description: ideaData.description || '',
        category: ideaData.category || 'general',
        priority: ideaData.priority || 'medium',
        tags: ideaData.tags || []
      });
      this.rebuildTagIndex();
      return result;
    },

    // Save user profile (for setup)
    async saveUserProfile(profileData) {
      if (!this.currentUserId) {
        console.error('❌ No user ID - cannot save profile');
        return false;
      }

      try {
        const profileRef = ref(database, `users/${this.currentUserId}/profile`);
        await set(profileRef, {
          ...profileData,
          setupCompleted: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        this.userProfile = { ...profileData, setupCompleted: true };
        console.log('✅ User profile saved successfully');
        return true;
      } catch (error) {
        console.error('❌ Error saving user profile:', error);
        return false;
      }
    },

    // Initialize with sample data (for testing)
    async initializeSampleData() {
      console.log('🔄 Initializing sample data...');
      try {
        // Add sample note
        await this.addNote({
          title: 'Welcome to Private App',
          content: 'This is your first note! You can create, edit, and organize all your thoughts here.',
          tags: ['welcome', 'getting-started']
        });

        // Add sample project
        await this.addProject({
          name: 'Learn Private App',
          description: 'Get familiar with all the features of your new Private App',
          status: 'in-progress',
          priority: 'high',
          tasks: [
            { id: 1, title: 'Complete setup', completed: true },
            { id: 2, title: 'Create first note', completed: false },
            { id: 3, title: 'Add a contact', completed: false }
          ]
        });

        // Add sample idea
        await this.addIdea({
          title: 'AI-Enhanced Note Taking',
          description: 'Use AI to automatically categorize and enhance notes with relevant information',
          category: 'productivity',
          priority: 'high'
        });
        
        console.log('✅ Sample data initialized successfully');
      } catch (error) {
        console.error('❌ Error initializing sample data:', error);
      }
    },

    // Load dynamic categories
    async loadDynamicCategories() {
      if (!this.currentUserId) return;
      
      try {
        const categoriesRef = ref(database, `users/${this.currentUserId}/knowledge`);
        
        // Get the list of categories once, then set up individual listeners
        const snapshot = await get(categoriesRef);
        const data = snapshot.val() || {};
        
        // Find all categories that aren't the default 4
        Object.keys(data).forEach(categoryName => {
          if (!['notes', 'projects', 'contacts', 'ideas'].includes(categoryName)) {
            console.log(`🔍 Setting up real-time listener for dynamic category: ${categoryName}`);
            
            // Initialize the category in local state
            if (!this.dynamicCategories[categoryName]) {
              this.dynamicCategories[categoryName] = [];
            }
            
            // Set up individual real-time listener for this dynamic category
            this.setupRealtimeListener(categoryName);
          }
        });
      } catch (error) {
        console.error('❌ Error loading dynamic categories:', error);
      }
    },

    // Create dynamic category
    async createCategory(categoryName) {
      if (!this.currentUserId) {
        throw new Error('No user ID - cannot create category');
      }

      const normalizedName = categoryName.toLowerCase().replace(/\s+/g, '-');
      
      try {
        // Initialize the category in Firebase
        const categoryRef = ref(database, `users/${this.currentUserId}/knowledge/${normalizedName}`);
        await set(categoryRef, {
          _metadata: {
            categoryName: categoryName,
            displayName: categoryName,
            createdAt: new Date().toISOString(),
            type: 'dynamic-category',
            itemCount: 0
          }
        });
        
        // Also create a category item for the carousel (gray card)
        const categoryItemRef = push(categoryRef, {
          title: categoryName,
          description: `Category created - ready for items`,
          type: 'category',
          image: '📁',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          userId: this.currentUserId,
          category: normalizedName,
          itemCount: 0,
          isCategory: true
        });
        
        // Initialize in local state
        this.dynamicCategories[normalizedName] = [];
        
        // Add green notification card for category creation
        this.addNotification(
          'category-created',
          '📁 Category Created',
          `"${categoryName}" is ready for items`,
          { categoryName, normalizedName }
        );
        
        console.log(`✅ Created dynamic category: ${categoryName}`);
        return normalizedName;
      } catch (error) {
        console.error(`❌ Error creating category ${categoryName}:`, error);
        throw error;
      }
    },

    // Add item to dynamic category
    async addItemToCategory(categoryName, item) {
      if (!this.currentUserId) {
        throw new Error('No user ID - cannot add item to category');
      }

      const normalizedName = categoryName.toLowerCase().replace(/\s+/g, '-');
      
      // Create category if it doesn't exist
      if (!this.dynamicCategories[normalizedName]) {
        await this.createCategory(categoryName);
      }

      const newItem = {
        ...item,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userId: this.currentUserId,
        category: normalizedName
      };

      try {
        const categoryRef = ref(database, `users/${this.currentUserId}/knowledge/${normalizedName}`);
        const newItemRef = push(categoryRef, newItem);
        
        const result = { id: newItemRef.key, ...newItem };
        
        // Add green notification for URL/item addition (but the actual item is also created)
        if (item.type === 'url') {
          this.addNotification(
            'url-added',
            '🔗 URL Added',
            `${item.title} added to "${categoryName}"`,
            { categoryName, itemId: result.id, url: item.url }
          );
        } else {
          this.addNotification(
            'item-added',
            '➕ Item Added',
            `"${item.title}" added to "${categoryName}"`,
            { categoryName, itemId: result.id }
          );
        }
        
        console.log(`✅ Added item to category "${categoryName}":`, item.title || item.name || item.url);
        return result;
      } catch (error) {
        console.error(`❌ Error adding item to category ${categoryName}:`, error);
        throw error;
      }
    },

    // Update item in Firebase
    async updateItem(category, itemId, updatedData) {
      if (!this.currentUserId || !itemId) {
        throw new Error('No user ID or item ID - cannot update item');
      }

      try {
        const itemRef = ref(database, `users/${this.currentUserId}/knowledge/${category}/${itemId}`);
        await set(itemRef, {
          ...updatedData,
          updatedAt: new Date().toISOString()
        });
        
        console.log(`✅ Updated ${category} item:`, itemId);
        this.rebuildTagIndex();
        return updatedData;
      } catch (error) {
        console.error(`❌ Error updating ${category} item:`, error);
        throw error;
      }
    },

    // Delete item from Firebase
    async deleteItem(category, itemId) {
      if (!this.currentUserId || !itemId) {
        throw new Error('No user ID or item ID - cannot delete item');
      }

      try {
        const itemRef = ref(database, `users/${this.currentUserId}/knowledge/${category}/${itemId}`);
        await remove(itemRef);
        
        console.log(`✅ Deleted ${category} item:`, itemId);
        this.rebuildTagIndex();
        return true;
      } catch (error) {
        console.error(`❌ Error deleting ${category} item:`, error);
        throw error;
      }
    },

    // Notification methods
    addNotification(type, title, message, data = {}) {
      const notification = {
        id: 'notif_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        type,
        title,
        message,
        data,
        createdAt: new Date().toISOString(),
        dismissed: false
      };
      
      this.notifications.unshift(notification);
      console.log(`📢 Notification added: ${title}`);
      return notification;
    },

    dismissNotification(notificationId) {
      const notification = this.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.dismissed = true;
        console.log(`🗑️ Notification dismissed: ${notification.title}`);
      }
    },

    clearDismissedNotifications() {
      const activeCount = this.notifications.filter(n => !n.dismissed).length;
      this.notifications = this.notifications.filter(n => !n.dismissed);
      console.log(`🧹 Cleared dismissed notifications, ${activeCount} remain active`);
    },

    // Clear all data
    clearAllData() {
      this.notes = [];
      this.projects = [];
      this.contacts = [];
      this.ideas = [];
      this.dynamicCategories = {};
      this.userProfile = null;
      this.isInitialized = false;
      this.currentUserId = null;
    }
  }
});